import { BhmrExternalDevice, dataOrderDetailTable } from "../../../../model/SaleOrderModel";

/**
 * Kiểm tra xem một sản phẩm có phải là Gói bảo hành mở rộng (BHMR) hay không.
 * Chỉ theo cờ isBhmrPackage (TBL_ITEM.ATTRIBUTE57 = 1, API search-sell / chi tiết đơn trả ra), không theo tiền tố mã:
 * nhiều mã tiền tố BH không phải gói BHMR
 */
export const isWarrantyItem = (item: any): boolean => item?.isBhmrPackage === true;

/**
 * Sản phẩm gán được gói BHMR: tạm thời mọi sản phẩm không phải gói BHMR, trừ dòng dịch vụ (DV) và dòng trả (HT).
 * Không nhận theo mã hay tên sản phẩm; thiết lập gói áp cho nhóm hàng nào làm sau.
 * (Giữ tên isLaptopItem vì các màn đang gọi)
 */
export const isLaptopItem = (item: any): boolean => {
  if (!item || isWarrantyItem(item)) return false;
  if (item.lineTyle === "DV" || item.lineTyle === "HT") return false;
  return !!(item.itemCode || item.productCode || "").trim();
};

/**
 * Lấy danh sách các mã gói bảo hành được gán cho Laptop từ attribute38
 */
export const getAssignedWarrantyCodes = (laptopItem: any): string[] => {
  if (!laptopItem || !laptopItem.attribute38) return [];
  return laptopItem.attribute38
    .split(",")
    .map((s: string) => s.trim())
    .filter(Boolean);
};

/**
 * Đếm số suất của 1 gói BH cụ thể được gán cho chiếc Laptop này
 */
export const countAssignedWarrantyForLaptop = (
  laptopItem: any,
  warrantyCode: string
): number => {
  const codes = getAssignedWarrantyCodes(laptopItem);
  const target = warrantyCode.trim().toUpperCase();
  return codes.filter((c) => c.toUpperCase() === target).length;
};

/**
 * Lấy danh sách các số serial máy cũ đã mua trước đó được lưu trên dòng gói BH (attribute38)
 */
export const getExternalSerialsForWarranty = (
  warrantyItem: dataOrderDetailTable | null | undefined
): string[] => {
  if (!warrantyItem || !warrantyItem.attribute38) return [];
  return warrantyItem.attribute38
    .split(",")
    .map((s: string) => s.trim())
    .filter(Boolean);
};

/**
 * Lấy thông tin máy ngoài đơn (bhmrExternalDevices) của dòng gói BH theo thứ tự serial máy cũ,
 * so serial không phân biệt hoa thường; serial chưa có thông tin bị bỏ qua
 */
export const getExternalDevicesForWarranty = (
  warrantyItem: dataOrderDetailTable | null | undefined
): BhmrExternalDevice[] => {
  const devices = warrantyItem?.bhmrExternalDevices || [];
  const result: BhmrExternalDevice[] = [];
  for (const serial of getExternalSerialsForWarranty(warrantyItem)) {
    const key = serial.toUpperCase();
    const device = devices.find(
      (d) => (d?.deviceSerial || "").trim().toUpperCase() === key
    );
    if (device) result.push(device);
  }
  return result;
};

/**
 * Tính tổng số suất đã phân bổ cho 1 gói BH trên toàn bộ đơn hàng
 * = (Tổng suất gán vào các Laptop trong đơn) + (Số lượng máy cũ nhập serial)
 */
export const getTotalAllocatedForWarranty = (
  listAdd: dataOrderDetailTable[],
  warrantyCode: string
): number => {
  const target = warrantyCode.trim().toUpperCase();
  let total = 0;

  for (const item of listAdd) {
    if (isLaptopItem(item)) {
      total += countAssignedWarrantyForLaptop(item, target);
    }
    // Đếm số lượng serial máy cũ nếu có trên dòng gói BH này
    if (
      isWarrantyItem(item) &&
      (item.itemCode || "").trim().toUpperCase() === target
    ) {
      total += getExternalSerialsForWarranty(item).length;
    }
  }

  return total;
};

/**
 * Tạo chuỗi tóm tắt phân bổ của 1 gói BH cho các dòng máy trong đơn
 * Ví dụ: "LTA1x1 LTA2x1 (2/2)" hoặc "Chưa phân bổ (0/2)"
 */
export const getWarrantyAllocationSummary = (
  listAdd: dataOrderDetailTable[],
  warrantyItem: dataOrderDetailTable
): string => {
  const wCode = (warrantyItem.itemCode || "").trim().toUpperCase();
  const parts: string[] = [];
  let totalAllocated = 0;

  for (const item of listAdd) {
    if (isLaptopItem(item)) {
      const count = countAssignedWarrantyForLaptop(item, wCode);
      if (count > 0) {
        parts.push(`${item.itemCode}*${count}`);
        totalAllocated += count;
      }
    }
  }

  // Bổ sung số lượng máy cũ nhập serial
  const oldSerials = getExternalSerialsForWarranty(warrantyItem);
  if (oldSerials.length > 0) {
    parts.push(`Serial*${oldSerials.length}`);
    totalAllocated += oldSerials.length;
  }

  // Hiển thị: "mã sp*số suất, Serial*số serial máy cũ (đã gán/tổng)"
  const summaryText = parts.length > 0 ? parts.join(", ") : "Chưa phân bổ";
  const qty = warrantyItem.quality || 1;
  return `${summaryText} (${totalAllocated}/${qty})`;
};

/**
 * Cập nhật số suất gán của 1 gói BH cho 1 chiếc Laptop
 * QUY TẮC: KHÔNG làm thay đổi số lượng (quality) của dòng gói bảo hành!
 */
export const setWarrantyAllocationForLaptop = (
  prevList: dataOrderDetailTable[],
  laptopId: number | undefined,
  warrantyCode: string,
  targetCount: number
): dataOrderDetailTable[] => {
  const wCode = warrantyCode.trim().toUpperCase();

  // Tìm gói bảo hành tương ứng để biết số lượng mua tối đa
  const warrantyItem = prevList.find(
    (it) => isWarrantyItem(it) && (it.itemCode || "").trim().toUpperCase() === wCode
  );
  const maxWarrantyCapacity = warrantyItem?.quality || 1;

  // Tính tổng số suất đã phân bổ cho gói BH này ở các laptop khác + máy cũ
  let allocatedElsewhere = 0;
  for (const item of prevList) {
    if (isLaptopItem(item) && item.id !== laptopId) {
      allocatedElsewhere += countAssignedWarrantyForLaptop(item, wCode);
    }
    if (
      isWarrantyItem(item) &&
      (item.itemCode || "").trim().toUpperCase() === wCode
    ) {
      allocatedElsewhere += getExternalSerialsForWarranty(item).length;
    }
  }

  const remainingWarrantySlots = Math.max(0, maxWarrantyCapacity - allocatedElsewhere);

  // Cập nhật attribute38 của Laptop
  return prevList.map((item) => {
    if (item.id === laptopId && isLaptopItem(item)) {
      const currentCodes = getAssignedWarrantyCodes(item);
      const otherCodes = currentCodes.filter((c) => c.toUpperCase() !== wCode);

      // Đảm bảo tổng số gói bảo hành không vượt quá số lượng máy Laptop
      const maxSlots = item.quality || 1;
      const remainingLaptopSlots = Math.max(0, maxSlots - otherCodes.length);

      // Giới hạn targetCount không vượt quá cả sức chứa máy và số lượng gói BH còn trống
      const safeTargetCount = Math.min(
        Math.max(0, targetCount),
        remainingLaptopSlots,
        remainingWarrantySlots
      );

      const newCodesForThis = Array(safeTargetCount).fill(warrantyCode.trim());
      const allCodes = [...otherCodes, ...newCodesForThis];

      return {
        ...item,
        attribute38: allCodes.join(", "),
      };
    }
    return item;
  });
};

/**
 * Gán nhanh 1 suất của gói BH cho 1 Laptop (đường chính - nút khiên)
 * QUY TẮC: KHÔNG làm thay đổi số lượng (quality) của dòng gói bảo hành!
 */
export const assignOneWarrantyToLaptop = (
  prevList: dataOrderDetailTable[],
  laptopId: number | undefined,
  warrantyCode: string
): { success: boolean; list: dataOrderDetailTable[]; message: string } => {
  const laptop = prevList.find((it) => it.id === laptopId);
  if (!laptop) {
    return {
      success: false,
      list: prevList,
      message: "Không tìm thấy sản phẩm Laptop trong đơn hàng!",
    };
  }

  const wCode = warrantyCode.trim().toUpperCase();
  const warrantyItem = prevList.find(
    (it) => isWarrantyItem(it) && (it.itemCode || "").trim().toUpperCase() === wCode
  );

  if (!warrantyItem) {
    return {
      success: false,
      list: prevList,
      message: `Không tìm thấy gói bảo hành ${warrantyCode} trong đơn hàng!`,
    };
  }

  // 1. Kiểm tra giới hạn máy Laptop
  const assignedCodes = getAssignedWarrantyCodes(laptop);
  const maxAllowedLaptop = laptop.quality || 1;
  if (assignedCodes.length >= maxAllowedLaptop) {
    return {
      success: false,
      list: prevList,
      message: `Laptop này đã được gán đủ ${maxAllowedLaptop}/${maxAllowedLaptop} suất bảo hành!`,
    };
  }

  // 2. Kiểm tra giới hạn số lượng của gói bảo hành
  const totalAllocated = getTotalAllocatedForWarranty(prevList, wCode);
  const maxWarrantyCapacity = warrantyItem.quality || 1;
  if (totalAllocated >= maxWarrantyCapacity) {
    return {
      success: false,
      list: prevList,
      message: `Gói bảo hành ${wCode} đã được phân bổ hết (${maxWarrantyCapacity}/${maxWarrantyCapacity} suất). Vui lòng tăng số lượng gói bảo hành ở cột 'Số lượng' nếu muốn gán thêm!`,
    };
  }

  const currentCount = countAssignedWarrantyForLaptop(laptop, warrantyCode);
  const updatedList = setWarrantyAllocationForLaptop(
    prevList,
    laptopId,
    warrantyCode,
    currentCount + 1
  );

  return {
    success: true,
    list: updatedList,
    message: `Đã gán 1 suất gói ${warrantyCode} cho ${laptop.itemName}!`,
  };
};

/**
 * Hủy gán 1 suất của gói BH khỏi 1 Laptop
 * QUY TẮC: KHÔNG làm thay đổi số lượng (quality) của dòng gói bảo hành!
 */
export const unassignOneWarrantyFromLaptop = (
  prevList: dataOrderDetailTable[],
  laptopId: number | undefined,
  warrantyCode: string
): dataOrderDetailTable[] => {
  const laptop = prevList.find((it) => it.id === laptopId);
  if (!laptop) return prevList;

  const currentCount = countAssignedWarrantyForLaptop(laptop, warrantyCode);
  if (currentCount <= 0) return prevList;

  return setWarrantyAllocationForLaptop(
    prevList,
    laptopId,
    warrantyCode,
    currentCount - 1
  );
};

/**
 * Dọn dẹp liên kết khi xóa 1 dòng sản phẩm khỏi giỏ hàng
 * QUY TẮC: KHÔNG làm thay đổi số lượng (quality) của các dòng sản phẩm còn lại!
 */
export const handleWarrantyOnDeleteRow = (
  prevList: dataOrderDetailTable[],
  deletedItem: dataOrderDetailTable
): dataOrderDetailTable[] => {
  // 1. Nếu xóa gói BH -> xóa mã gói đó khỏi attribute38 của tất cả các dòng Laptop
  if (isWarrantyItem(deletedItem)) {
    const deletedCode = (deletedItem.itemCode || "").trim().toUpperCase();
    return prevList.map((item) => {
      if (isLaptopItem(item) && item.attribute38) {
        const remainingCodes = getAssignedWarrantyCodes(item).filter(
          (c) => c.toUpperCase() !== deletedCode
        );
        return {
          ...item,
          attribute38: remainingCodes.join(", "),
        };
      }
      return item;
    });
  }

  // 2. Nếu xóa Laptop -> Các suất của gói BH được giải phóng (quality của gói BH giữ nguyên)
  return prevList;
};

/**
 * Xử lý khi số lượng Laptop thay đổi (giảm SL Laptop thì tự động cắt bớt suất gói BH thừa trên máy đó)
 * QUY TẮC: KHÔNG làm thay đổi số lượng (quality) của dòng gói bảo hành!
 */
export const handleLaptopQuantityChangeWarrantySync = (
  prevList: dataOrderDetailTable[],
  laptopId: number | undefined,
  newQuantity: number
): dataOrderDetailTable[] => {
  const laptop = prevList.find((it) => it.id === laptopId);
  if (!laptop || !isLaptopItem(laptop)) return prevList;

  const assignedCodes = getAssignedWarrantyCodes(laptop);
  if (assignedCodes.length <= newQuantity) {
    return prevList; // Chưa vượt quá số lượng máy mới
  }

  // Cắt bớt phần vượt quá trên máy Laptop
  const trimmedCodes = assignedCodes.slice(0, Math.max(0, newQuantity));
  return prevList.map((item) => {
    if (item.id === laptopId) {
      return {
        ...item,
        attribute38: trimmedCodes.join(", "),
      };
    }
    return item;
  });
};

/**
 * Xử lý khi số lượng gói bảo hành bị giảm ở cột "Số lượng":
 * Nếu số lượng mới nhỏ hơn tổng số suất đã phân bổ, thu hồi các suất phân bổ thừa từ các máy.
 */
export const handleWarrantyQuantityChangeSync = (
  prevList: dataOrderDetailTable[],
  warrantyItemCode: string,
  newQuantity: number
): { list: dataOrderDetailTable[]; trimmedCount: number } => {
  const wCode = warrantyItemCode.trim().toUpperCase();
  const safeNewQty = Math.max(0, newQuantity);

  const currentAllocated = getTotalAllocatedForWarranty(prevList, wCode);
  if (currentAllocated <= safeNewQty) {
    return { list: prevList, trimmedCount: 0 };
  }

  let excess = currentAllocated - safeNewQty;
  const trimmedCount = excess;

  // 1. Thu hồi từ serials máy cũ trước (nếu có)
  let updatedList = prevList.map((item) => {
    if (
      isWarrantyItem(item) &&
      (item.itemCode || "").trim().toUpperCase() === wCode &&
      excess > 0
    ) {
      const serials = getExternalSerialsForWarranty(item);
      if (serials.length > 0) {
        const removeCount = Math.min(excess, serials.length);
        const keptSerials = serials.slice(0, serials.length - removeCount);
        excess -= removeCount;
        return {
          ...item,
          attribute38: keptSerials.join(", "),
          note:
            keptSerials.length > 0 ? `Máy cũ: ${keptSerials.join(", ")}` : "",
        };
      }
    }
    return item;
  });

  // 2. Thu hồi từ các Laptop (từ dưới lên)
  if (excess > 0) {
    const reversedLaptops = updatedList
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => isLaptopItem(item))
      .reverse();

    for (const { item, idx } of reversedLaptops) {
      if (excess <= 0) break;
      const count = countAssignedWarrantyForLaptop(item, wCode);
      if (count > 0) {
        const removeCount = Math.min(excess, count);
        const codes = getAssignedWarrantyCodes(item);
        let removedSoFar = 0;
        const newCodes = [];
        for (let i = codes.length - 1; i >= 0; i--) {
          if (codes[i].toUpperCase() === wCode && removedSoFar < removeCount) {
            removedSoFar++;
          } else {
            newCodes.unshift(codes[i]);
          }
        }
        excess -= removeCount;
        updatedList[idx] = {
          ...updatedList[idx],
          attribute38: newCodes.join(", "),
        };
      }
    }
  }

  return { list: updatedList, trimmedCount };
};

/*
 * ĐỊNH DẠNG LƯU DB (01/10/2026). Chỉ đổi lúc gửi đơn lên API và lúc nạp đơn, màn hình vẫn dùng cách ở trên:
 * - Trên màn: attribute38 dòng Laptop = mã gói (mỗi suất 1 lần), attribute38 dòng gói = serial máy cũ.
 * - Lưu DB: dữ liệu nằm trên dòng gói. attribute38 = máy trong đơn "ITEM_CODE*số suất, ...",
 *   attribute39 = serial máy ngoài đơn "SN1, SN2". Dòng Laptop không lưu gì.
 * - bhmrExternalDevices trên dòng gói (màn và khi gửi API) = mã/tên của từng máy ngoài đơn, API lưu TBL_BHMR_EXTERNAL_DEVICE.
 */

const splitList = (raw?: string | null): string[] =>
  (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Đổi danh sách dòng trên màn sang định dạng lưu DB, gọi ngay trước khi gửi orderDetail lên API.
 * Không sửa danh sách gốc. Suất của nhiều dòng gói cùng mã dồn vào dòng gói đầu tiên có mã đó.
 */
export const toStoredWarranty = <T extends dataOrderDetailTable>(list: T[]): T[] => {
  // mã gói (viết hoa) -> mã máy (viết hoa) -> { mã máy như trên dòng, số suất }
  const slotsByWarranty = new Map<string, Map<string, { code: string; slots: number }>>();
  for (const item of list) {
    if (!isLaptopItem(item)) continue;
    const laptopCode = (item.itemCode || "").trim();
    if (!laptopCode) continue;
    for (const wCode of getAssignedWarrantyCodes(item)) {
      const wKey = wCode.toUpperCase();
      let byLaptop = slotsByWarranty.get(wKey);
      if (!byLaptop) {
        byLaptop = new Map();
        slotsByWarranty.set(wKey, byLaptop);
      }
      const entry = byLaptop.get(laptopCode.toUpperCase());
      if (entry) entry.slots += 1;
      else byLaptop.set(laptopCode.toUpperCase(), { code: laptopCode, slots: 1 });
    }
  }

  const writtenWarranty = new Set<string>();
  return list.map((item) => {
    if (isWarrantyItem(item)) {
      const wKey = (item.itemCode || "").trim().toUpperCase();
      const byLaptop = writtenWarranty.has(wKey) ? undefined : slotsByWarranty.get(wKey);
      writtenWarranty.add(wKey);
      return {
        ...item,
        attribute38: byLaptop
          ? Array.from(byLaptop.values())
              .map((e) => `${e.code}*${e.slots}`)
              .join(", ")
          : "",
        attribute39: getExternalSerialsForWarranty(item).join(", "),
        // Thông tin máy theo serial; serial chưa có thông tin thì API giữ bản ghi đã lưu của đơn, chưa có thì ghi tên tạm
        bhmrExternalDevices: getExternalDevicesForWarranty(item),
      };
    }
    if (isLaptopItem(item)) {
      return { ...item, attribute38: "" };
    }
    return item;
  });
};

/**
 * Đổi danh sách dòng tải từ API (định dạng lưu DB) về cách màn đang dùng, gọi ngay khi nạp đơn vào listAdd.
 * Đơn chưa có dữ liệu định dạng mới (dòng gói không có "*" trong attribute38 và attribute39 rỗng) giữ nguyên.
 * Suất rải xuống các dòng Laptop cùng mã theo thứ tự, mỗi dòng tối đa bằng số lượng; còn dư dồn vào dòng cuối.
 */
export const fromStoredWarranty = <T extends dataOrderDetailTable>(list: T[]): T[] => {
  if (!Array.isArray(list)) return list;
  const isStoredFormat = list.some(
    (item) =>
      isWarrantyItem(item) &&
      (splitList(item.attribute38).some((t) => t.includes("*")) ||
        splitList(item.attribute39).length > 0)
  );
  if (!isStoredFormat) return list;

  const result: T[] = list.map((item) => {
    if (isWarrantyItem(item)) {
      const tokens = splitList(item.attribute38);
      const storedSerials = splitList(item.attribute39);
      const serials =
        storedSerials.length > 0
          ? storedSerials
          : tokens.some((t) => t.includes("*"))
            ? []
            : tokens;
      return { ...item, attribute38: serials.join(", ") };
    }
    if (isLaptopItem(item)) {
      return { ...item, attribute38: "" };
    }
    return item;
  });

  for (const warranty of list) {
    if (!isWarrantyItem(warranty)) continue;
    const wCode = (warranty.itemCode || "").trim();
    for (const token of splitList(warranty.attribute38)) {
      const star = token.lastIndexOf("*");
      if (star <= 0) continue;
      const laptopKey = token.slice(0, star).trim().toUpperCase();
      let remaining = parseInt(token.slice(star + 1).trim(), 10);
      if (!laptopKey || !(remaining > 0)) continue;

      const targetIndexes = result
        .map((item, idx) => ({ item, idx }))
        .filter(
          ({ item }) =>
            isLaptopItem(item) && (item.itemCode || "").trim().toUpperCase() === laptopKey
        )
        .map(({ idx }) => idx);

      targetIndexes.forEach((idx, n) => {
        if (remaining <= 0) return;
        const current = getAssignedWarrantyCodes(result[idx]);
        const isLast = n === targetIndexes.length - 1;
        const free = isLast ? remaining : Math.max(0, (result[idx].quality || 1) - current.length);
        const take = Math.min(free, remaining);
        if (take <= 0) return;
        result[idx] = {
          ...result[idx],
          attribute38: [...current, ...Array(take).fill(wCode)].join(", "),
        };
        remaining -= take;
      });
    }
  }

  return result;
};

/**
 * Như fromStoredWarranty nhưng tách theo orderHeaderId: dùng cho danh sách gộp dòng của nhiều đơn
 * (đổi trả: dòng đơn trả T, đơn gốc G, đơn lịch sử H), để suất chỉ rải xuống laptop cùng đơn.
 */
export const fromStoredWarrantyByOrder = <T extends dataOrderDetailTable>(list: T[]): T[] => {
  if (!Array.isArray(list)) return list;
  const groups = new Map<number | null, number[]>();
  list.forEach((item, index) => {
    const key = item.orderHeaderId ?? null;
    const group = groups.get(key);
    if (group) group.push(index);
    else groups.set(key, [index]);
  });
  const result = list.slice();
  groups.forEach((indexes) => {
    const converted = fromStoredWarranty(indexes.map((index) => list[index]));
    indexes.forEach((index, n) => {
      result[index] = converted[n];
    });
  });
  return result;
};

/**
 * Gói BHMR chưa phân bổ đủ suất (đã gán cho laptop + serial máy cũ < số lượng gói), dùng để chặn chốt đơn.
 * Gộp các dòng cùng mã gói. Bỏ qua dòng đơn gốc của luồng đổi trả (attribute11 G/H/T) vì không được lưu.
 */
export const getUnderAllocatedWarranties = (
  listAdd: dataOrderDetailTable[]
): { code: string; allocated: number; quantity: number }[] => {
  const rows = listAdd.filter((item) => !["G", "H", "T"].includes(item.attribute11 ?? ""));
  const quantityByCode = new Map<string, { code: string; quantity: number }>();
  for (const item of rows) {
    if (!isWarrantyItem(item)) continue;
    const code = (item.itemCode || "").trim();
    const entry = quantityByCode.get(code.toUpperCase());
    if (entry) entry.quantity += Number(item.quality) || 0;
    else quantityByCode.set(code.toUpperCase(), { code, quantity: Number(item.quality) || 0 });
  }
  return Array.from(quantityByCode.values())
    .map(({ code, quantity }) => ({ code, quantity, allocated: getTotalAllocatedForWarranty(rows, code) }))
    .filter((item) => item.allocated < item.quantity);
};
